import React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import formatDate from "date-fns/format";
import getMonth from "date-fns/getMonth";
import getYear from "date-fns/getYear";
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridToolbarFilterButton,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import { Theme } from "@mui/material/styles";
import { CustomExportButton } from "@app.automotus.io/components/CustomExportButton";
import MonthYearSelectMenu from "@app.automotus.io/components/MonthYearSelectMenu/MonthYearSelectMenu";
import { ColumnsToolbarButton, ResetLayoutButton } from "../DataTableContainer";

export interface PayerTableToolbarProps {
  exportFileName?: string;
  columns: GridColDef[];
  fetchTotalCount: () => Promise<number>;
  fetchDataBatch: (offset: number, limit: number) => Promise<unknown[]>;
  batchSize: number;
  selectedDate: Date;
  onChangeSelectedDate: (d: Date) => void;
  maxDate: Date;
  /** The table's default column visibility — see useHiddenColumnsCount's docs on why this must stay a stable, code-defined constant. */
  defaultVisibilityModel: GridColumnVisibilityModel;
  /** Below 700px, collapses the export button to icon-only. The filter button collapses via its own responsive sx below. */
  isIconOnly?: boolean;
  /** Clears this table's saved layout and restores its defaults — see DataTableContainerChildrenParams.resetLayout. */
  resetLayout: () => void;
  hideMonthSelector?: boolean;
}

export const PayerTableToolbar: React.FC<PayerTableToolbarProps> = ({
  exportFileName,
  columns,
  fetchTotalCount,
  fetchDataBatch,
  batchSize,
  selectedDate,
  onChangeSelectedDate,
  maxDate,
  defaultVisibilityModel,
  isIconOnly,
  resetLayout,
  hideMonthSelector,
}) => {
  const apiRef = useGridApiContext();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        height: 52,
        px: 1.25,
        bgcolor: (theme: Theme) => theme.palette.background.paper,
      }}
    >
      <Stack direction="row" justifyContent="flex-start" alignItems="center" height="100%" spacing={1}>
        <GridToolbarFilterButton
          componentsProps={{
            button: {
              sx: {
                minWidth: { xs: "auto", md: 64 },
                px: { xs: 1, md: 1.25 },
                fontSize: "14px",
                textTransform: "none",
                "& .MuiButton-startIcon": { mr: { xs: 0, md: 0.5 }, ml: { xs: 0, md: -0.25 } },
                "& .MuiButton-startIcon svg": { fontSize: "20px" },
              },
            },
          }}
        />
        <ColumnsToolbarButton apiRef={apiRef} defaultVisibilityModel={defaultVisibilityModel} />
        <CustomExportButton
          getTotalCount={fetchTotalCount}
          fetchDataBatch={fetchDataBatch}
          fileName={exportFileName}
          columns={columns}
          aria-label="Export"
          buttonLabel={isIconOnly ? "" : "Export"}
          sx={{
            minWidth: { xs: "auto", md: 64 },
            px: { xs: 1, md: 1.25 },
            fontSize: "14px",
            fontWeight: 400,
            letterSpacing: 0.15,
            textTransform: "none",
            "& .MuiButton-startIcon": { mr: { xs: 0, md: 0.5 }, ml: { xs: 0, md: -0.25 } },
            "& .MuiButton-startIcon svg": { fontSize: "20px" },
          }}
          batchSize={batchSize}
        />
        <ResetLayoutButton onReset={resetLayout} isIconOnly={isIconOnly} />
      </Stack>
      {!hideMonthSelector && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            id="payer-table-month-select-button"
            aria-controls={open ? "payer-table-month-select-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            variant="text"
            size="large"
            endIcon={<ArrowDropDownIcon />}
            sx={{ textTransform: "none" }}
            onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
          >
            {formatDate(selectedDate, "MMMM yyyy")}
          </Button>
          <MonthYearSelectMenu
            month={getMonth(selectedDate)}
            year={getYear(selectedDate)}
            maxDate={maxDate}
            open={open}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            onChange={(d) => {
              onChangeSelectedDate(d);
              setAnchorEl(null);
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          />
        </Stack>
      )}
    </Box>
  );
};

export default PayerTableToolbar;
